import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { useGetUserQuery } from 'api';
import { GlobalSearchPalette } from 'components/admin/GlobalSearchPalette';
import LoadingScreen from 'components/LoadingScreen';
import ScrollToTop from 'components/ScrollToTop';
import { useIsCurrentUserAdmin } from 'hooks/slices';
import { useShouldAcceptCgu } from 'hooks/useShouldAcceptCgu';
import { AcceptCguPage } from 'pages/shared/AcceptCguPage';
import { useTrackPage } from 'utils/tracking';

interface MyRouterContext {
  redirectUrl: string | null;
}

const RootComponent = () => {
  // NOTE: useTrackPage is called here at the root level, so it may track a page view even if the
  // actual page is not rendered (e.g., due to AcceptCguPage or a redirect).
  // Ideally, page tracking should be done closer to where the final page is actually rendered, to
  // avoid tracking pages that the user never sees.
  useTrackPage();

  const shouldAcceptCguState = useShouldAcceptCgu();
  const isCurrentUserAdmin = useIsCurrentUserAdmin();

  // Hack to prevent a redirection if we are fetching the user
  const { isLoading } = useGetUserQuery({ enabled: false });

  if (isLoading || shouldAcceptCguState.isLoading) return <LoadingScreen />;

  if (shouldAcceptCguState.shouldAcceptCgu) return <AcceptCguPage />;

  return (
    <>
      <ScrollToTop />
      {isCurrentUserAdmin && <GlobalSearchPalette />}
      <Outlet />
    </>
  );
};

export const rootRoute = createRootRouteWithContext<MyRouterContext>()({
  loader: () => undefined, // see https://github.com/TanStack/router/issues/3556,
  component: RootComponent,
});
